import type { SecurityIssuedIn } from '@dealstack/prisma-client';
import { SecurityFeature } from './securityFeature';

/**
 * `'UNITS'` is written out rather than read off a value-imported enum. This
 * module sits on the backend's boot path (via `SecurityFeatureRulesService`),
 * and a runtime import of `@dealstack/shared/lib/generated` fails during
 * `generate-swagger`, which boots the app before the generated types exist.
 * The type annotation still pins the literal to the DB enum, so a rename in
 * the Prisma schema breaks the build here rather than silently at runtime.
 */
const ISSUED_IN_UNITS: `${SecurityIssuedIn}` = 'UNITS';

export type StateCondition =
	| 'ISSUED_IN_UNITS'
	| 'RESIDUE_PARTICIPATION'
	| 'NO_RESIDUE_PARTICIPATION'
	| 'NOT_EXCHANGE_TARGET';

export type DeferredPredicate = 'NO_PERFORMANCE_VESTING';

export type SecurityBaseField =
	| 'residueRights'
	| 'issuedIn'
	| 'equityBasis'
	| 'nominalValue'
	| 'votesPerUnit'
	| 'hasNomineeStructure';

export type FieldLockCondition =
	| 'IS_EXCHANGE_TARGET'
	| 'NOT_ISSUED_IN_UNITS'
	| 'NO_RESIDUE_PARTICIPATION'
	| 'ISSUER_LACKS_NOMINEE_STRUCTURE';

export interface FeatureRule {
	needs?: readonly (StateCondition | SecurityFeature)[];
	excludes?: readonly SecurityFeature[];
	clears?: readonly StateCondition[];
	deferred?: readonly DeferredPredicate[];
}

export interface FieldLockRule {
	lockedWhen: readonly FieldLockCondition[];
}

export type BlockReason =
	| { type: 'MISSING_STATE'; condition: StateCondition }
	| { type: 'MISSING_FEATURE'; feature: SecurityFeature }
	| { type: 'EXCLUDED_FEATURE'; feature: SecurityFeature }
	| {
			type: 'CONTRADICTORY_STATE';
			condition: StateCondition;
			feature: SecurityFeature;
	  }
	| { type: 'DEFERRED_PREDICATE'; predicate: DeferredPredicate }
	| { type: 'FIELD_LOCK'; condition: FieldLockCondition };

export interface SecurityStateSnapshot {
	issuedIn: `${SecurityIssuedIn}`;
	residueRights: boolean;
	present: ReadonlySet<SecurityFeature>;
	isExchangeTarget: boolean;
	issuerHasNomineeStructure: boolean;
}

export interface SecurityFeatureSnapshotInput {
	issuedIn: `${SecurityIssuedIn}`;
	residueRights: boolean;
	isExchangeTarget: boolean;
	issuerHasNomineeStructure: boolean;
	hasAuthorisedCapital: boolean;
	hasInterestDefinition: boolean;
	hasTracking: boolean;
	hasRedemptionRights: boolean;
	hasCatchUpRights: boolean;
	hasExchangeRights: boolean;
	hasLiquidationPreference: boolean;
	hasParticipationThreshold: boolean;
	hasRatcheting: boolean;
}

export interface StateTransitionIntent {
	explicitlyEnabled: ReadonlySet<StateCondition>;
}

interface StateInvariantViolation {
	condition: StateCondition;
	requirement: StateCondition;
}

export const makeSecurityStateSnapshot = (
	input: SecurityFeatureSnapshotInput
): SecurityStateSnapshot => {
	const present = new Set<SecurityFeature>();
	const features: Array<[SecurityFeature, boolean]> = [
		[SecurityFeature.AUTHORISED_CAPITAL, input.hasAuthorisedCapital],
		[SecurityFeature.INCOME_RIGHTS_INTEREST, input.hasInterestDefinition],
		[SecurityFeature.INCOME_RIGHTS_TRACKING, input.hasTracking],
		[SecurityFeature.REDEMPTION_RIGHTS, input.hasRedemptionRights],
		[SecurityFeature.CATCH_UP_RIGHTS, input.hasCatchUpRights],
		[SecurityFeature.EXCHANGE_RIGHTS, input.hasExchangeRights],
		[SecurityFeature.LIQUIDATION_PREFERENCE, input.hasLiquidationPreference],
		[SecurityFeature.PARTICIPATION_THRESHOLD, input.hasParticipationThreshold],
		[SecurityFeature.RATCHETING, input.hasRatcheting],
	];

	for (const [feature, isPresent] of features) {
		if (isPresent) {
			present.add(feature);
		}
	}

	return {
		issuedIn: input.issuedIn,
		residueRights: input.residueRights,
		present,
		isExchangeTarget: input.isExchangeTarget,
		issuerHasNomineeStructure: input.issuerHasNomineeStructure,
	};
};

export const STATE_INVARIANTS: Partial<Record<StateCondition, readonly StateCondition[]>> = {
	RESIDUE_PARTICIPATION: ['ISSUED_IN_UNITS'],
};

export const SECURITY_FEATURE_RULES: Record<SecurityFeature, FeatureRule> = {
	[SecurityFeature.AUTHORISED_CAPITAL]: {},
	[SecurityFeature.INCOME_RIGHTS_INTEREST]: {
		excludes: [SecurityFeature.INCOME_RIGHTS_TRACKING, SecurityFeature.EXCHANGE_RIGHTS],
	},
	[SecurityFeature.INCOME_RIGHTS_TRACKING]: {
		needs: ['ISSUED_IN_UNITS', 'NO_RESIDUE_PARTICIPATION'],
	},
	[SecurityFeature.REDEMPTION_RIGHTS]: {
		needs: [SecurityFeature.INCOME_RIGHTS_INTEREST],
	},
	[SecurityFeature.CATCH_UP_RIGHTS]: {
		excludes: [SecurityFeature.EXCHANGE_RIGHTS],
	},
	[SecurityFeature.EXCHANGE_RIGHTS]: {
		needs: ['ISSUED_IN_UNITS', 'NOT_EXCHANGE_TARGET'],
		clears: ['RESIDUE_PARTICIPATION'],
	},
	[SecurityFeature.LIQUIDATION_PREFERENCE]: {
		needs: ['RESIDUE_PARTICIPATION'],
	},
	[SecurityFeature.PARTICIPATION_THRESHOLD]: {
		needs: ['RESIDUE_PARTICIPATION'],
		excludes: [SecurityFeature.RATCHETING],
	},
	[SecurityFeature.RATCHETING]: {
		needs: ['RESIDUE_PARTICIPATION'],
		deferred: ['NO_PERFORMANCE_VESTING'],
	},
};

export const FIELD_LOCK_MATRIX: Record<SecurityBaseField, FieldLockRule> = {
	residueRights: {
		lockedWhen: ['IS_EXCHANGE_TARGET', 'NOT_ISSUED_IN_UNITS'],
	},
	issuedIn: {
		lockedWhen: ['IS_EXCHANGE_TARGET'],
	},
	equityBasis: {
		lockedWhen: ['NO_RESIDUE_PARTICIPATION'],
	},
	nominalValue: {
		lockedWhen: ['NOT_ISSUED_IN_UNITS'],
	},
	votesPerUnit: {
		lockedWhen: ['NOT_ISSUED_IN_UNITS'],
	},
	hasNomineeStructure: {
		lockedWhen: ['NOT_ISSUED_IN_UNITS', 'ISSUER_LACKS_NOMINEE_STRUCTURE'],
	},
};

const isStateCondition = (
	requirement: StateCondition | SecurityFeature
): requirement is StateCondition =>
	requirement === 'ISSUED_IN_UNITS' ||
	requirement === 'RESIDUE_PARTICIPATION' ||
	requirement === 'NO_RESIDUE_PARTICIPATION' ||
	requirement === 'NOT_EXCHANGE_TARGET';

const conditionIsMet = (condition: StateCondition, snapshot: SecurityStateSnapshot): boolean => {
	switch (condition) {
		case 'ISSUED_IN_UNITS':
			return snapshot.issuedIn === ISSUED_IN_UNITS;
		case 'RESIDUE_PARTICIPATION':
			return snapshot.residueRights;
		case 'NO_RESIDUE_PARTICIPATION':
			return !snapshot.residueRights;
		case 'NOT_EXCHANGE_TARGET':
			return !snapshot.isExchangeTarget;
	}
};

const lockConditionIsMet = (
	condition: FieldLockCondition,
	snapshot: SecurityStateSnapshot
): boolean => {
	switch (condition) {
		case 'IS_EXCHANGE_TARGET':
			return snapshot.isExchangeTarget;
		case 'NOT_ISSUED_IN_UNITS':
			return snapshot.issuedIn !== ISSUED_IN_UNITS;
		case 'NO_RESIDUE_PARTICIPATION':
			return !snapshot.residueRights;
		case 'ISSUER_LACKS_NOMINEE_STRUCTURE':
			return !snapshot.issuerHasNomineeStructure;
	}
};

const stateRequirements = (
	feature: SecurityFeature,
	visited: ReadonlySet<SecurityFeature> = new Set()
): Set<StateCondition> => {
	if (visited.has(feature)) {
		return new Set();
	}

	const nextVisited = new Set(visited);
	nextVisited.add(feature);
	const conditions = new Set<StateCondition>();

	for (const requirement of SECURITY_FEATURE_RULES[feature].needs ?? []) {
		if (isStateCondition(requirement)) {
			conditions.add(requirement);
			for (const invariant of STATE_INVARIANTS[requirement] ?? []) {
				conditions.add(invariant);
			}
		} else {
			for (const condition of stateRequirements(requirement, nextVisited)) {
				conditions.add(condition);
			}
		}
	}

	return conditions;
};

const oppositeCondition = (condition: StateCondition): StateCondition | undefined => {
	if (condition === 'RESIDUE_PARTICIPATION') {
		return 'NO_RESIDUE_PARTICIPATION';
	}
	if (condition === 'NO_RESIDUE_PARTICIPATION') {
		return 'RESIDUE_PARTICIPATION';
	}
	return undefined;
};

const declaredExclusion = (feature: SecurityFeature, other: SecurityFeature): boolean =>
	SECURITY_FEATURE_RULES[feature].excludes?.includes(other) === true ||
	SECURITY_FEATURE_RULES[other].excludes?.includes(feature) === true;

export const evaluateFeature = (
	feature: SecurityFeature,
	snapshot: SecurityStateSnapshot
): { allowed: boolean; blockedBy: BlockReason[] } => {
	const blockedBy: BlockReason[] = [];
	const featureRule = SECURITY_FEATURE_RULES[feature];

	for (const requirement of featureRule.needs ?? []) {
		if (isStateCondition(requirement)) {
			const requiredConditions = [requirement, ...(STATE_INVARIANTS[requirement] ?? [])];
			for (const condition of requiredConditions) {
				if (!conditionIsMet(condition, snapshot)) {
					blockedBy.push({ type: 'MISSING_STATE', condition });
				}
			}
		} else if (!snapshot.present.has(requirement)) {
			blockedBy.push({ type: 'MISSING_FEATURE', feature: requirement });
		}
	}

	const ownStateRequirements = stateRequirements(feature);
	for (const presentFeature of snapshot.present) {
		if (presentFeature === feature) {
			continue;
		}

		if (declaredExclusion(feature, presentFeature)) {
			blockedBy.push({ type: 'EXCLUDED_FEATURE', feature: presentFeature });
			continue;
		}

		for (const condition of ownStateRequirements) {
			const opposite = oppositeCondition(condition);
			if (opposite && stateRequirements(presentFeature).has(opposite)) {
				blockedBy.push({
					type: 'CONTRADICTORY_STATE',
					condition,
					feature: presentFeature,
				});
			}
		}
	}

	for (const predicate of featureRule.deferred ?? []) {
		blockedBy.push({ type: 'DEFERRED_PREDICATE', predicate });
	}

	return {
		allowed: blockedBy.every((reason) => reason.type === 'DEFERRED_PREDICATE'),
		blockedBy,
	};
};

export const evaluateFieldLock = (
	field: SecurityBaseField,
	snapshot: SecurityStateSnapshot
): { locked: boolean; reason?: BlockReason } => {
	const condition = FIELD_LOCK_MATRIX[field].lockedWhen.find((candidate) =>
		lockConditionIsMet(candidate, snapshot)
	);

	return condition
		? { locked: true, reason: { type: 'FIELD_LOCK', condition } }
		: { locked: false };
};

const findStateInvariantViolations = (
	snapshot: SecurityStateSnapshot
): StateInvariantViolation[] => {
	const violations: StateInvariantViolation[] = [];
	const invariants = Object.entries(STATE_INVARIANTS) as [
		StateCondition,
		readonly StateCondition[],
	][];

	for (const [condition, requirements] of invariants) {
		if (!conditionIsMet(condition, snapshot)) {
			continue;
		}

		for (const requirement of requirements) {
			if (!conditionIsMet(requirement, snapshot)) {
				violations.push({ condition, requirement });
			}
		}
	}

	return violations;
};

export const evaluateStateInvariants = (snapshot: SecurityStateSnapshot): BlockReason[] =>
	findStateInvariantViolations(snapshot).map(({ requirement }) => ({
		type: 'MISSING_STATE',
		condition: requirement,
	}));

export const evaluateStateTransition = (
	prev: SecurityStateSnapshot,
	next: SecurityStateSnapshot,
	intent: StateTransitionIntent
): { allowed: boolean; blockedBy: BlockReason[] } => {
	const blocking = findStateInvariantViolations(next).filter(
		({ condition, requirement }) =>
			intent.explicitlyEnabled.has(condition) || !conditionIsMet(requirement, prev)
	);
	const blockedBy: BlockReason[] = blocking.map(({ requirement }) => ({
		type: 'MISSING_STATE',
		condition: requirement,
	}));

	return { allowed: blockedBy.length === 0, blockedBy };
};

const withNormalizedState = (
	snapshot: SecurityStateSnapshot,
	present: ReadonlySet<SecurityFeature>
): { snapshot: SecurityStateSnapshot; clearedStates: Set<StateCondition> } => {
	let residueRights = snapshot.residueRights;
	const clearedStates = new Set<StateCondition>();
	const invariantSnapshot = (): SecurityStateSnapshot => ({
		...snapshot,
		residueRights,
		present,
	});

	for (const { condition } of findStateInvariantViolations(invariantSnapshot())) {
		switch (condition) {
			case 'RESIDUE_PARTICIPATION':
				residueRights = false;
				clearedStates.add(condition);
				break;
			case 'ISSUED_IN_UNITS':
			case 'NO_RESIDUE_PARTICIPATION':
			case 'NOT_EXCHANGE_TARGET':
				throw new Error(`State invariant owner ${condition} cannot be cascade-cleared`);
		}
	}

	for (const feature of present) {
		for (const condition of SECURITY_FEATURE_RULES[feature].clears ?? []) {
			if (condition === 'RESIDUE_PARTICIPATION' && residueRights) {
				residueRights = false;
				clearedStates.add(condition);
			}
		}
	}

	return {
		snapshot: { ...snapshot, residueRights, present },
		clearedStates,
	};
};

export const featuresLosingRequirements = (
	prev: SecurityStateSnapshot,
	next: SecurityStateSnapshot
): { clearedStates: StateCondition[]; features: SecurityFeature[] } => {
	const remaining = new Set(next.present);
	const removed = new Set<SecurityFeature>();
	// Cascade removals driven by an added feature's excludes edge. Tracked separately so the tail
	// "un-remove caller-dropped" loop doesn't reinstate them when the caller passes a next.present
	// that already omits the excluded feature.
	const excludedByNewlyAdded = new Set<SecurityFeature>();

	for (const added of next.present) {
		if (prev.present.has(added)) {
			continue;
		}
		for (const other of [...remaining]) {
			if (other === added) {
				continue;
			}
			if (declaredExclusion(added, other)) {
				remaining.delete(other);
				removed.add(other);
				excludedByNewlyAdded.add(other);
			}
		}
	}

	for (const feature of next.present) {
		const residueWasExplicitlyEnabled =
			!prev.residueRights && next.residueRights && prev.present.has(feature);
		if (
			residueWasExplicitlyEnabled &&
			SECURITY_FEATURE_RULES[feature].clears?.includes('RESIDUE_PARTICIPATION')
		) {
			remaining.delete(feature);
			removed.add(feature);
		}
	}

	let normalized = withNormalizedState(next, remaining);
	let changed = true;

	while (changed) {
		changed = false;

		for (const feature of [...remaining]) {
			const featureSnapshot = {
				...normalized.snapshot,
				present: remaining,
			};
			const losesRequirement = (SECURITY_FEATURE_RULES[feature].needs ?? []).some(
				(requirement) =>
					isStateCondition(requirement)
						? [requirement, ...(STATE_INVARIANTS[requirement] ?? [])].some(
								(condition) => !conditionIsMet(condition, featureSnapshot)
							)
						: !remaining.has(requirement)
			);

			if (losesRequirement) {
				remaining.delete(feature);
				removed.add(feature);
				changed = true;
			}
		}

		const nextNormalized = withNormalizedState(next, remaining);
		for (const clearedState of nextNormalized.clearedStates) {
			normalized.clearedStates.add(clearedState);
		}
		normalized = {
			snapshot: nextNormalized.snapshot,
			clearedStates: normalized.clearedStates,
		};
	}

	for (const feature of prev.present) {
		if (!next.present.has(feature) && !excludedByNewlyAdded.has(feature)) {
			removed.delete(feature);
		}
	}

	return {
		clearedStates: [...normalized.clearedStates],
		features: [...removed],
	};
};

/**
 * Preview of what enabling a feature would do, from the button-click screen. Splits blockers into
 * hard (user must fix elsewhere) vs. cascade-resolvable (confirmable in the popup), and simulates
 * the resulting cascade so the caller can render "these features/states will be cleared".
 */
export const previewFeatureAddition = (
	feature: SecurityFeature,
	snapshot: SecurityStateSnapshot
): {
	hardBlockers: BlockReason[];
	residueWouldFlipOn: boolean;
	residueWouldFlipOff: boolean;
	cascade: { clearedStates: StateCondition[]; features: SecurityFeature[] };
} => {
	const { blockedBy } = evaluateFeature(feature, snapshot);

	const hardBlockers = blockedBy.filter((reason) => {
		if (reason.type === 'MISSING_FEATURE') {
			return true;
		}
		if (reason.type === 'MISSING_STATE') {
			return (
				reason.condition === 'ISSUED_IN_UNITS' || reason.condition === 'NOT_EXCHANGE_TARGET'
			);
		}
		return false;
	});

	// Residue flips either way when the feature's needs contradict the current state — this must be
	// reflected in the simulated `next` so the feature itself doesn't appear in cascade.features
	// (it would if its own NO_RESIDUE_PARTICIPATION need was unmet on the unchanged snapshot).
	const featureStates = stateRequirements(feature);
	const residueWouldFlipOn =
		!snapshot.residueRights && featureStates.has('RESIDUE_PARTICIPATION');
	const residueWouldFlipOff =
		snapshot.residueRights && featureStates.has('NO_RESIDUE_PARTICIPATION');
	const nextResidue = residueWouldFlipOn || (!residueWouldFlipOff && snapshot.residueRights);

	const next: SecurityStateSnapshot = {
		...snapshot,
		present: new Set([...snapshot.present, feature]),
		residueRights: nextResidue,
	};

	const cascade = featuresLosingRequirements(snapshot, next);

	return {
		hardBlockers,
		residueWouldFlipOn,
		residueWouldFlipOff,
		cascade,
	};
};
